import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalUser } from '../types';
import { authApi, ApiError } from '../utils/api';

interface PortalAuthContextValue {
  user: PortalUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<PortalUser>;
  register: (payload: {
    role: 'student' | 'educator';
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    institution_id: string;
  }) => Promise<{ user: PortalUser | null; pendingApproval: boolean; message?: string }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<PortalUser | null>(null);

  const authStateQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: 1,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!authStateQuery.data) return;
    setUser(authStateQuery.data.authenticated && authStateQuery.data.user ? authStateQuery.data.user : null);
  }, [authStateQuery.data]);

  useEffect(() => {
    if (!authStateQuery.error) return;
    setUser(null);
    const error = authStateQuery.error;
    if (!(error instanceof ApiError) || error.status >= 500) {
      console.error('Failed to fetch auth state:', error);
    }
  }, [authStateQuery.error]);

  const signInMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => authApi.login(email, password),
    onSuccess: (response) => {
      setUser(response.user);
      queryClient.setQueryData(['auth', 'me'], { authenticated: true, user: response.user });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (payload: {
      role: 'student' | 'educator';
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      institution_id: string;
    }) => authApi.register(payload),
    onSuccess: (response) => {
      if (response.pending_approval) {
        setUser(null);
        queryClient.setQueryData(['auth', 'me'], { authenticated: false, user: undefined });
        return;
      }

      setUser(response.user);
      queryClient.setQueryData(['auth', 'me'], { authenticated: true, user: response.user });
    },
  });

  const signOutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setUser(null);
      queryClient.setQueryData(['auth', 'me'], { authenticated: false, user: undefined });
    },
  });

  const refreshUser = useCallback(async () => {
    await authStateQuery.refetch();
  }, [authStateQuery]);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await signInMutation.mutateAsync({ email, password });
    return response.user;
  }, [signInMutation]);

  const register = useCallback(async (payload: {
    role: 'student' | 'educator';
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    institution_id: string;
  }) => {
    const response = await registerMutation.mutateAsync(payload);
    return {
      user: response.pending_approval ? null : response.user,
      pendingApproval: Boolean(response.pending_approval),
      message: response.message,
    };
  }, [registerMutation]);

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const loading = authStateQuery.isLoading;

  const value = useMemo(
    () => ({ user, loading, signIn, register, signOut, refreshUser }),
    [loading, refreshUser, register, signIn, signOut, user]
  );

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return context;
}
